import { Epic, ofType } from 'redux-observable';
import { Observable, of, empty, from, EMPTY } from 'rxjs';
import { switchMap, mergeAll, map, concatMap, tap, withLatestFrom, filter, catchError } from 'rxjs/operators';

import { InquiriesAction } from './inquiries.actions';
import * as InquiriesActions from './inquiries.actions';
import { INQUIRY_PAGE_STAGES } from '../../../../constants/inquiries';
import { inquiryService } from '../../../utils/inquiry.service';
import { InquiryStageDefinition, InquiryListResponse, InquiryStage } from '../../../../types/inquiries';
import { appState$ } from '../../store';
import commentService from '../../../utils/comment.service';

const inquiriesEffects: Epic[] = [];


/** Dispatch load actions for all stages. */
const loadAllStages: Epic = loadAllStages$ => loadAllStages$.pipe(
  ofType<InquiriesAction>(InquiriesActions.LOAD_ALL_STAGES),
  switchMap((action) => {
    const filters = action.payload.filters;
    return from(INQUIRY_PAGE_STAGES).pipe(
      map(stageDefn => InquiriesActions.loadStage(stageDefn.slug, filters))
    );
  }),
  // tap(console.log)
);

const loadStage: Epic = loadStage$ => loadStage$.pipe(
  ofType<InquiriesAction>(InquiriesActions.LOAD_STAGE),
  // tap(console.log),
  concatMap(action =>
    from(
      inquiryService.getByStage(action.stageSlug, action.payload.filters)
    ).pipe(
      map(inquiries => InquiriesActions.storeStage(action.stageSlug, inquiries))
    )
  ),
  // tap(console.log)
);

const loadMoreForStage: Epic = loadMoreForStage$ => loadMoreForStage$.pipe(
  ofType<InquiriesAction>(InquiriesActions.LOAD_MORE_FOR_STAGE),
  withLatestFrom(appState$),
  filter(([action, appState]) => {
    const inquiries = appState.inquiries.stageInquiries[action.stageSlug];
    return inquiries.results.length < inquiries.count;
  }),
  concatMap(([action, appState]) => {
    const slug = action.stageSlug;
    const inqStore = appState.inquiries;
    const curInquiries = inqStore.stageInquiries[slug];
    const page = inqStore.stagePages[action.stageSlug];

    const req = inquiryService.getByStage(
      action.stageSlug, {
        page: page// ,
        // ...this.state.filters
      }
    );

    return from(req).pipe(
      map(inquiries => {
        inquiries.results = [ ...curInquiries.results, ...inquiries.results ];
        return inquiries;
      }),
      map(inquiries => InquiriesActions.storeStage(slug, inquiries))
    );
  }),
);


inquiriesEffects.push(
  selectStage$ => selectStage$.pipe(
    ofType<InquiriesAction>(InquiriesActions.SELECT_STAGE),
    withLatestFrom(appState$.pipe(
      map(appState => appState.inquiries)
    )),
    map(([action, inquiries]) => InquiriesActions.loadStage(action.stageSlug, inquiries.filters))
  )
);


const selectInquiry: Epic = selectInquiry$ => selectInquiry$.pipe(
  ofType<InquiriesAction>(InquiriesActions.SELECT_INQUIRY),
  map(action => InquiriesActions.createLoadActionsFor(action.payload.selectedInquiry)),
  mergeAll()
);

inquiriesEffects.push(
  search$ => search$.pipe(
    ofType<InquiriesAction>(InquiriesActions.SEARCH),
    withLatestFrom(appState$.pipe(
      map(appState => appState.inquiries)
    )),
    switchMap(([action, inquiries]) => {
      const { searchString } = action.payload;
      return inquiryService.search(searchString, 'all', action.payload.filters || inquiries.filters);
    }),
    // tap(console.log),
    map(results => InquiriesActions.storeSearch(results))
  )
);


inquiriesEffects.push(
  selectInquiry$ => selectInquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.LOAD_FTF),
    switchMap(action =>
      from(inquiryService.getInquiryFtf(action.payload.selectedInquiry.inquiryCode)).pipe(
        map(ftf => InquiriesActions.storeFTF({ ftf })),
        catchError(() => of({ type: InquiriesActions.LOAD_FTF_FAILED }))
      )
    ),
  )
);

inquiriesEffects.push(
  selectInquiry$ => selectInquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.LOAD_COMMENTS),
    switchMap(action => from(commentService.getComments(
      action.payload.selectedInquiry.inquiryCode,
      'inquiry'
    )).pipe(
      map(response => response.data),
      map(comments => InquiriesActions.storeComments({ comments })),
      catchError(() => {
        return of({ type: InquiriesActions.LOAD_COMMENTS_FAILED });
      })
    ))
  )
);

inquiriesEffects.push(
  selectInquiry$ => selectInquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.LOAD_SELECTED_PROPERTIES),
    switchMap(action => 
      from(inquiryService.getSelectedProperties(action.payload.selectedInquiry.inquiryCode)).pipe(
        map(selectedProperties => InquiriesActions.storeSelectedProperties({ selectedProperties })),
        catchError(() => of({ type: InquiriesActions.LOAD_SELECTED_PROPERTIES_FAILED }))
      )
    )
  )
)

inquiriesEffects.push(
  selectInquiry$ => selectInquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.LOAD_VIEWING_LISTS),
    switchMap(action =>
      from(inquiryService.getViewingLists(action.payload.selectedInquiry.inquiryCode)).pipe(
        map(viewingLists => InquiriesActions.storeViewingLists({ viewingLists })),
        catchError(() => of({ type: InquiriesActions.LOAD_VIEWING_LISTS_FAILED }))
      )
    )
  )
);

inquiriesEffects.push(
  selectInquiry$ => selectInquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.LOAD_OFFERS),
    switchMap(action =>
      from(inquiryService.getOfferList(action.payload.selectedInquiry.inquiryCode)).pipe(
        map(offers => InquiriesActions.storeOffers({ offers })),
        catchError(() => of({ type: InquiriesActions.LOAD_OFFERS_FAILED }))
      )
    )
  )
);

inquiriesEffects.push(
  selectInquiry$ => selectInquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.LOAD_CONTRACTS),
    switchMap(action =>
      from(inquiryService.getContracts(action.payload.selectedInquiry.inquiryCode)).pipe(
        map(contracts => InquiriesActions.storeContracts({ contracts })),
        catchError(() => of({ type: InquiriesActions.LOAD_CONTRACTS_FAILED }))
      )
    )
  )
);

inquiriesEffects.push(
  attachmentUpdated$ => attachmentUpdated$.pipe(
    ofType<InquiriesAction>(InquiriesActions.ATTACHMENTS_UPDATED_FOR_INQUIRY),
    withLatestFrom(appState$.pipe(map(s => s.inquiries))),
    filter(
      ([action, inquiries]) =>
        action.payload.selectedInquiry.inquiryCode === inquiries.selectedInquiry.inquiryCode
    ),
    map(([action, inquiries]) => InquiriesActions.selectInquiry(inquiries.selectedInquiry))
  )
);

inquiriesEffects.push(
  inquiry$ => inquiry$.pipe(
    ofType<InquiriesAction>(InquiriesActions.RELOAD_SELECTED_INQUIRY),
    withLatestFrom(appState$.pipe(map(s => s.inquiries))),
    filter(([action, inquiries]) => action.payload.selectedInquiry.inquiryCode == inquiries.selectedInquiry.inquiryCode),
    switchMap(([action, inquiries]) => inquiryService.getInquiry(inquiries.selectedInquiry.inquiryCode)),
    map(inquiry => [
      InquiriesActions.storeSelectedInquiry(inquiry),
      ...InquiriesActions.createLoadActionsFor(inquiry)
    ]),
    mergeAll()
  )
);

inquiriesEffects.push(loadAllStages);
inquiriesEffects.push(loadStage);
inquiriesEffects.push(loadMoreForStage);
inquiriesEffects.push(selectInquiry);

export default inquiriesEffects;
